// The doors a mail opens (#415): the screens behind the links the outbox carries.
//
// Five, one per link the server renders (`mail/links.py`): the invitation, the
// verification, the reset and the page that asks for it, and the sign-in link.
// Each is rendered **before the gate** (`App.tsx`), because a person holding an
// invitation may have no session and a verification link must work with or
// without one. Every screen has the four states the ticket asks for: what it
// is doing, what it needs, what went wrong and what to do next, and what it
// did — and every next step is a real link to a screen that exists.

import { useRef, useState, type FormEvent, type ReactNode } from "react";

import { addressRefusal, messageOf } from "../api/http";
import type { SessionResponse } from "../api/session";
import { Centre, Field, Notice } from "./chrome";
import type { DoorState } from "./consoleState";
import type { DoorId } from "./routes";
import type { ConsoleActions } from "./useConsole";

/** The person at the door, when there is one. */
export type Holder = { signedIn: false } | { signedIn: true; session: SessionResponse };

export function Door({
  door,
  token,
  state,
  holder,
  actions,
}: {
  door: DoorId;
  token: string | null;
  state: DoorState | null;
  holder: Holder;
  actions: ConsoleActions;
}) {
  switch (door) {
    case "invite":
      return <Invitation token={token} state={state} holder={holder} actions={actions} />;
    case "verify":
      return <Verify state={state} holder={holder} actions={actions} />;
    case "magic":
      return <Magic state={state} />;
    case "forgot":
      return <Forgot state={state} actions={actions} />;
    case "reset":
      return <Reset token={token} state={state} actions={actions} />;
  }
}

/** Where a finished door sends the person: the workspace, or the sign-in screen. */
function Next({ next }: { next: "runs" | "sign-in" | null }) {
  if (next === null) return null;
  return (
    <p className="actions">
      <a className="btn btn--primary" href="/">
        {next === "runs" ? "Go to your workspace" : "Sign in"}
      </a>
    </p>
  );
}

function Busy({ children }: { children: ReactNode }) {
  return (
    <p className="muted" role="status">
      {children}
    </p>
  );
}

// -------------------------------------------------------------- invitation --

function Invitation({
  token,
  state,
  holder,
  actions,
}: {
  token: string | null;
  state: DoorState | null;
  holder: Holder;
  actions: ConsoleActions;
}) {
  if (state?.kind === "done") {
    return (
      <Centre title="Welcome aboard" lede={state.message}>
        <Next next={state.next} />
      </Centre>
    );
  }
  if (state?.kind === "failed") {
    return (
      <Centre
        title="That invitation is not valid"
        lede="It may have been used, retracted, or left to expire. Ask the person who invited you for a new one."
      >
        <Notice tone="bad">{state.message}</Notice>
        <p className="actions">
          <a className="btn" href="/">
            {holder.signedIn ? "Go to your workspace" : "Sign in"}
          </a>
        </p>
      </Centre>
    );
  }
  if (state?.kind !== "invitation") {
    return (
      <Centre title="Opening your invitation">
        <Busy>Reading what this link opens…</Busy>
      </Centre>
    );
  }
  const { invite } = state;
  const lede = (
    <>
      You are invited to <strong>{invite.workspace_name}</strong> as <strong>{invite.role}</strong>.
      The invitation is for <strong>{invite.email}</strong>.
    </>
  );
  if (holder.signedIn && holder.session.user.email.toLowerCase() !== invite.email) {
    return (
      <Centre title="You're invited" lede={lede}>
        <Notice>
          You are signed in as {holder.session.user.email}, which is not the address this
          invitation was sent to. Sign out, then open the link again.
        </Notice>
        <p className="actions">
          <button type="button" className="btn" onClick={() => void actions.signOut()}>
            Sign out
          </button>
        </p>
      </Centre>
    );
  }
  if (holder.signedIn) {
    return (
      <Centre title="You're invited" lede={lede}>
        <AcceptForm token={token} onAccept={actions.acceptInvite} />
      </Centre>
    );
  }
  if (invite.account_exists) {
    return (
      <Centre title="You're invited" lede={lede}>
        <SignInToAccept email={invite.email} token={token} actions={actions} />
      </Centre>
    );
  }
  return (
    <Centre title="You're invited" lede={lede}>
      <RegisterWithInvite email={invite.email} token={token} actions={actions} />
    </Centre>
  );
}

function AcceptForm({
  token,
  onAccept,
}: {
  token: string | null;
  onAccept: (token: string) => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const accept = async () => {
    if (token === null) return;
    setError(null);
    setBusy(true);
    try {
      await onAccept(token);
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      <p className="actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || token === null}
          onClick={() => void accept()}
        >
          {busy ? "Joining…" : "Accept invitation"}
        </button>
      </p>
    </>
  );
}

/** An address with an account, not signed in: sign in here, then the acceptance follows. */
function SignInToAccept({
  email,
  token,
  actions,
}: {
  email: string;
  token: string | null;
  actions: ConsoleActions;
}) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (token === null) return;
    setError(null);
    setBusy(true);
    try {
      await actions.signIn({ email, password });
      await actions.acceptInvite(token);
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} aria-busy={busy}>
      <Notice>This address already has an account. Sign in to accept.</Notice>
      <Field id="email" label="Email" name="email" type="email" value={email} readOnly />
      <Field
        id="password"
        label="Password"
        name="password"
        type="password"
        autoComplete="current-password"
        required
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        error={error}
      />
      <div className="actions">
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? "Signing in…" : "Sign in and accept"}
        </button>
        <a className="link link--muted" href="/forgot">
          Forgotten your password?
        </a>
      </div>
    </form>
  );
}

/** A stranger: an account is created with the invitation, verified by it. */
function RegisterWithInvite({
  email,
  token,
  actions,
}: {
  email: string;
  token: string | null;
  actions: ConsoleActions;
}) {
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const emailInput = useRef<HTMLInputElement>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (token === null) return;
    setError(null);
    setEmailError(null);
    setBusy(true);
    try {
      await actions.registerWithInvite({
        email,
        password,
        display_name: displayName.trim() === "" ? null : displayName.trim(),
        token,
      });
    } catch (failure) {
      const refused = addressRefusal(failure, "email");
      if (refused === null) setError(messageOf(failure));
      else {
        setEmailError(refused);
        emailInput.current?.focus();
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} aria-busy={busy}>
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      <Field
        id="email"
        ref={emailInput}
        label="Email"
        name="email"
        type="email"
        value={email}
        readOnly
        error={emailError}
        hint="The invitation was sent to this address, and that is the address the account gets."
      />
      <Field
        id="display-name"
        label="Your name"
        name="display_name"
        autoComplete="name"
        value={displayName}
        onChange={(event) => setDisplayName(event.target.value)}
      />
      <Field
        id="password"
        label="Choose a password"
        name="password"
        type="password"
        autoComplete="new-password"
        required
        minLength={12}
        hint="At least 12 characters."
        value={password}
        onChange={(event) => setPassword(event.target.value)}
      />
      <div className="actions">
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? "Creating your account…" : "Create your account"}
        </button>
      </div>
    </form>
  );
}

// ------------------------------------------------------------ verification --

function Verify({
  state,
  holder,
  actions,
}: {
  state: DoorState | null;
  holder: Holder;
  actions: ConsoleActions;
}) {
  if (state?.kind === "done") {
    return (
      <Centre title="Email verified" lede={state.message}>
        <Next next={holder.signedIn ? "runs" : "sign-in"} />
      </Centre>
    );
  }
  if (state?.kind === "failed") {
    return (
      <Centre
        title="That link did not work"
        lede="Verification links work once and expire after a day."
      >
        <Notice tone="bad">{state.message}</Notice>
        {holder.signedIn ? (
          <ResendVerification onResend={actions.resendVerification} />
        ) : (
          <p className="actions">
            <a className="btn" href="/">
              Sign in to request a new link
            </a>
          </p>
        )}
      </Centre>
    );
  }
  return (
    <Centre title="Verifying your email">
      <Busy>One moment…</Busy>
    </Centre>
  );
}

/** The resend, shared by the failed verification door and the console's banner. */
export function ResendVerification({ onResend }: { onResend: () => Promise<void> }) {
  const [state, setState] = useState<"idle" | "busy" | "sent" | "failed">("idle");
  const [error, setError] = useState<string | null>(null);
  const resend = async () => {
    setState("busy");
    setError(null);
    try {
      await onResend();
      setState("sent");
    } catch (failure) {
      setError(messageOf(failure));
      setState("failed");
    }
  };
  if (state === "sent") return <Notice tone="good">A new verification link is on its way.</Notice>;
  return (
    <div className="actions">
      <button
        type="button"
        className="btn btn--small"
        disabled={state === "busy"}
        onClick={() => void resend()}
      >
        {state === "busy" ? "Sending…" : "Send a new verification link"}
      </button>
      {error === null ? null : <span className="field__error">{error}</span>}
    </div>
  );
}

// -------------------------------------------------------------- magic link --

function Magic({ state }: { state: DoorState | null }) {
  if (state?.kind === "done") {
    return (
      <Centre title="Signed in" lede={state.message}>
        <Next next="runs" />
      </Centre>
    );
  }
  if (state?.kind === "failed") {
    return (
      <Centre title="That link did not work" lede="Sign-in links work once and expire after a short while.">
        <Notice tone="bad">{state.message}</Notice>
        <p className="actions">
          <a className="btn" href="/">
            Sign in another way
          </a>
        </p>
      </Centre>
    );
  }
  return (
    <Centre title="Signing you in">
      <Busy>One moment…</Busy>
    </Centre>
  );
}

// ------------------------------------------------------- forgot and reset --

function Forgot({ state, actions }: { state: DoorState | null; actions: ConsoleActions }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const emailInput = useRef<HTMLInputElement>(null);
  if (state?.kind === "done") {
    return (
      <Centre title="Check your inbox" lede={state.message}>
        <p className="actions">
          <a className="link" href="/">
            Back to sign in
          </a>
        </p>
      </Centre>
    );
  }
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setEmailError(null);
    setBusy(true);
    try {
      await actions.requestReset(email);
    } catch (failure) {
      const refused = addressRefusal(failure, "email");
      if (refused === null) setError(messageOf(failure));
      else {
        setEmailError(refused);
        emailInput.current?.focus();
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <Centre
      title="Forgotten your password?"
      lede="Enter your email and we will send a link to choose a new one."
    >
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      <form onSubmit={submit} aria-busy={busy}>
        <Field
          id="email"
          ref={emailInput}
          label="Email"
          name="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={emailError}
        />
        <div className="actions">
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "Sending…" : "Send the link"}
          </button>
          <a className="link link--muted" href="/">
            Back to sign in
          </a>
        </div>
      </form>
    </Centre>
  );
}

function Reset({
  token,
  state,
  actions,
}: {
  token: string | null;
  state: DoorState | null;
  actions: ConsoleActions;
}) {
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (state?.kind === "done") {
    return (
      <Centre title="Password changed" lede={state.message}>
        <Next next="sign-in" />
      </Centre>
    );
  }
  if (token === null) {
    return (
      <Centre title="That link is missing its token" lede="Open the link exactly as it was sent, or ask for a new one.">
        <p className="actions">
          <a className="btn" href="/forgot">
            Ask for a new link
          </a>
        </p>
      </Centre>
    );
  }
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== again) {
      setError("The two passwords do not match.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await actions.resetPassword(token, password);
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Centre title="Choose a new password" lede="Every other session on this account will be signed out.">
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      <form onSubmit={submit} aria-busy={busy}>
        <Field
          id="password"
          label="New password"
          name="new_password"
          type="password"
          autoComplete="new-password"
          required
          minLength={12}
          hint="At least 12 characters."
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <Field
          id="password-again"
          label="New password, again"
          name="new_password_again"
          type="password"
          autoComplete="new-password"
          required
          value={again}
          onChange={(event) => setAgain(event.target.value)}
        />
        <div className="actions">
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "Changing…" : "Change password"}
          </button>
          <a className="link link--muted" href="/forgot">
            The link expired? Ask for a new one
          </a>
        </div>
      </form>
    </Centre>
  );
}
